import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { authApi, dashboardApi, headerApi } from './services';
// Imported from the module (not the services barrel) to avoid ambiguous hook
// re-exports — several endpoint hook names collide with dashboardApi's.
import { projectBoardApi } from './services/projectBoardApi';
import { ahuBoardApi } from './services/ahuBoardApi';
import { reportsBoardApi } from './services/reportsBoardApi';
import { cmsApi } from './services/cmsApi';
import authReducer from './slices/authSlice';
import dashboardReducer from './slices/dashboardSlice';
import projectBoardReducer from './slices/projectBoardSlice';
import ahuBoardReducer from './slices/ahuBoardSlice';
import ahuValidationReducer from './slices/ahuValidationSlice';

// KAI Select (ported from the standalone react-js-dev KAI Selector widget —
// see pages/kai-select/). Its own ~28 RTK Query APIs + ~27 plain slices, one
// per AHU component type, matching the source project's flat state shape
// (state.fan, state.coil, ... — see pages/kai-select/models/store.ts's
// IState for the authoritative key list). Its own authApi/authSlice were
// dropped as dead code in favor of this app's real auth (nothing in the
// ported UI reads state.auth or calls its login/logout hooks).
import { accessDoorApi } from '../pages/kai-select/services/accessDoorApi';
import { adpGageApi } from '../pages/kai-select/services/adpGageApi';
import { ahuApi } from '../pages/kai-select/services/ahuApi';
import { coilApi } from '../pages/kai-select/services/coilApi';
import { damperApi } from '../pages/kai-select/services/damperApi';
import { disconnectApi } from '../pages/kai-select/services/disconnectApi';
import { drainPanApi } from '../pages/kai-select/services/drainPanApi';
import { eliminatorApi } from '../pages/kai-select/services/eliminatorApi';
import { enclosureApi } from '../pages/kai-select/services/enclosureApi';
import { erWheelApi } from '../pages/kai-select/services/erWheelApi';
import { evapPackApi } from '../pages/kai-select/services/evapPackApi';
import { fanApi } from '../pages/kai-select/services/fanApi';
import { filterApi } from '../pages/kai-select/services/filterApi';
import { gasHeaterApi } from '../pages/kai-select/services/gasHeaterApi';
import { hoodApi } from '../pages/kai-select/services/hoodApi';
import { jboxApi } from '../pages/kai-select/services/jboxApi';
import { lightApi } from '../pages/kai-select/services/lightApi';
import { louverApi } from '../pages/kai-select/services/louverApi';
import { openingApi } from '../pages/kai-select/services/openingApi';
import { plateHeatexApi } from '../pages/kai-select/services/plateHeatexApi';
import { projectApi as kaiProjectApi } from '../pages/kai-select/services/projectApi';
import { reportApi as kaiReportApi } from '../pages/kai-select/services/reportApi';
import { silencerApi } from '../pages/kai-select/services/silencerApi';
import { starterApi } from '../pages/kai-select/services/starterApi';
import { udaApi } from '../pages/kai-select/services/udaApi';
import { uvcApi } from '../pages/kai-select/services/uvcApi';
import { vfdApi } from '../pages/kai-select/services/vfdApi';
import { kaiSizerApi } from '../pages/kai-select/services/sizerApi';

import kaiAccessDoorReducer from '../pages/kai-select/slices/accessDoorSlice';
import kaiAdpGageReducer from '../pages/kai-select/slices/adpGageSlice';
import kaiAhuReducer from '../pages/kai-select/slices/ahuSlice';
import kaiCoilReducer from '../pages/kai-select/slices/coilSlice';
import kaiDamperReducer from '../pages/kai-select/slices/damperSlice';
import kaiDisconnectReducer from '../pages/kai-select/slices/disconnectSlice';
import kaiDrainPanReducer from '../pages/kai-select/slices/drainPanSlice';
import kaiEliminatorReducer from '../pages/kai-select/slices/eliminatorSlice';
import kaiEnclosureReducer from '../pages/kai-select/slices/enclosureSlice';
import kaiErWheelReducer from '../pages/kai-select/slices/erWheelSlice';
import kaiEvapPackReducer from '../pages/kai-select/slices/evapPackSlice';
import kaiFanReducer from '../pages/kai-select/slices/fanSlice';
import kaiFilterReducer from '../pages/kai-select/slices/filterSlice';
import kaiGasHeaterReducer from '../pages/kai-select/slices/gasHeaterSlice';
import kaiHoodReducer from '../pages/kai-select/slices/hoodSlice';
import kaiJboxReducer from '../pages/kai-select/slices/jboxSlice';
import kaiLightReducer from '../pages/kai-select/slices/lightSlice';
import kaiLouverReducer from '../pages/kai-select/slices/louverSlice';
import kaiOpeningReducer from '../pages/kai-select/slices/openingSlice';
import kaiPlateHeatexReducer from '../pages/kai-select/slices/plateHeatexSlice';
import kaiProjectsReducer from '../pages/kai-select/slices/projectSlice';
import kaiReportReducer from '../pages/kai-select/slices/reportSlice';
import kaiSilencerReducer from '../pages/kai-select/slices/silencerSlice';
import kaiStarterReducer from '../pages/kai-select/slices/starterSlice';
import kaiUdaReducer from '../pages/kai-select/slices/udaSlice';
import kaiUvcReducer from '../pages/kai-select/slices/uvcSlice';
import kaiVfdReducer from '../pages/kai-select/slices/vfdSlice';

// Each phase adds its own API/slice as it's built fresh from the Backbone original.
const rootReducer = combineReducers({
  auth: authReducer,
  dashboard: dashboardReducer,
  projectBoard: projectBoardReducer,
  ahuBoard: ahuBoardReducer,
  ahuValidation: ahuValidationReducer,
  [authApi.reducerPath]: authApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [headerApi.reducerPath]: headerApi.reducer,
  [projectBoardApi.reducerPath]: projectBoardApi.reducer,
  [ahuBoardApi.reducerPath]: ahuBoardApi.reducer,
  [reportsBoardApi.reducerPath]: reportsBoardApi.reducer,
  // CMS Admin (Phase 7). ONE api for all 31 modules; each tranche adds its own
  // endpoints with `cmsApi.injectEndpoints` — see app/services/cmsApi.ts (D-1).
  [cmsApi.reducerPath]: cmsApi.reducer,

  // KAI Select — flat per-component-type state, matching the source
  // project's own store shape (pages/kai-select/models/store.ts IState).
  ahu: kaiAhuReducer,
  projects: kaiProjectsReducer,
  fan: kaiFanReducer,
  coil: kaiCoilReducer,
  gasHeater: kaiGasHeaterReducer,
  erWheel: kaiErWheelReducer,
  plateHeatex: kaiPlateHeatexReducer,
  filter: kaiFilterReducer,
  eliminator: kaiEliminatorReducer,
  evapPack: kaiEvapPackReducer,
  silencer: kaiSilencerReducer,
  accessDoor: kaiAccessDoorReducer,
  opening: kaiOpeningReducer,
  damper: kaiDamperReducer,
  louver: kaiLouverReducer,
  hood: kaiHoodReducer,
  adpGage: kaiAdpGageReducer,
  jbox: kaiJboxReducer,
  disconnect: kaiDisconnectReducer,
  enclosure: kaiEnclosureReducer,
  vfd: kaiVfdReducer,
  starter: kaiStarterReducer,
  light: kaiLightReducer,
  drainPan: kaiDrainPanReducer,
  uvc: kaiUvcReducer,
  uda: kaiUdaReducer,
  report: kaiReportReducer,
  [accessDoorApi.reducerPath]: accessDoorApi.reducer,
  [adpGageApi.reducerPath]: adpGageApi.reducer,
  [ahuApi.reducerPath]: ahuApi.reducer,
  [coilApi.reducerPath]: coilApi.reducer,
  [damperApi.reducerPath]: damperApi.reducer,
  [disconnectApi.reducerPath]: disconnectApi.reducer,
  [drainPanApi.reducerPath]: drainPanApi.reducer,
  [eliminatorApi.reducerPath]: eliminatorApi.reducer,
  [enclosureApi.reducerPath]: enclosureApi.reducer,
  [erWheelApi.reducerPath]: erWheelApi.reducer,
  [evapPackApi.reducerPath]: evapPackApi.reducer,
  [fanApi.reducerPath]: fanApi.reducer,
  [filterApi.reducerPath]: filterApi.reducer,
  [gasHeaterApi.reducerPath]: gasHeaterApi.reducer,
  [hoodApi.reducerPath]: hoodApi.reducer,
  [jboxApi.reducerPath]: jboxApi.reducer,
  [lightApi.reducerPath]: lightApi.reducer,
  [louverApi.reducerPath]: louverApi.reducer,
  [openingApi.reducerPath]: openingApi.reducer,
  [plateHeatexApi.reducerPath]: plateHeatexApi.reducer,
  [kaiProjectApi.reducerPath]: kaiProjectApi.reducer,
  [kaiReportApi.reducerPath]: kaiReportApi.reducer,
  [silencerApi.reducerPath]: silencerApi.reducer,
  [starterApi.reducerPath]: starterApi.reducer,
  [udaApi.reducerPath]: udaApi.reducer,
  [uvcApi.reducerPath]: uvcApi.reducer,
  [vfdApi.reducerPath]: vfdApi.reducer,
  [kaiSizerApi.reducerPath]: kaiSizerApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,
      dashboardApi.middleware,
      headerApi.middleware,
      projectBoardApi.middleware,
      ahuBoardApi.middleware,
      reportsBoardApi.middleware,
      cmsApi.middleware,
      accessDoorApi.middleware,
      adpGageApi.middleware,
      ahuApi.middleware,
      coilApi.middleware,
      damperApi.middleware,
      disconnectApi.middleware,
      drainPanApi.middleware,
      eliminatorApi.middleware,
      enclosureApi.middleware,
      erWheelApi.middleware,
      evapPackApi.middleware,
      fanApi.middleware,
      filterApi.middleware,
      gasHeaterApi.middleware,
      hoodApi.middleware,
      jboxApi.middleware,
      lightApi.middleware,
      louverApi.middleware,
      openingApi.middleware,
      plateHeatexApi.middleware,
      kaiProjectApi.middleware,
      kaiReportApi.middleware,
      silencerApi.middleware,
      starterApi.middleware,
      udaApi.middleware,
      uvcApi.middleware,
      vfdApi.middleware,
      kaiSizerApi.middleware
    ),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;

setupListeners(store.dispatch);
